<template>
    <div class="w-full mb-4">
        <!-- Date Header with proper positioning -->
        <div class="relative">
            <div
                class="bg-slate-100 dark:bg-slate-900 p-2 mb-4 relative z-10"
                v-if="!sensitiveDataHidden"
            >
                <h6 class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                    {{ dateSegment.date }}
                </h6>
            </div>
        </div>

        <!-- Timeline container with proper line structure -->
        <div class="relative">
            <!-- Timeline line connecting all icons -->
            <div
                v-if="
                    dateSegment.redemptions &&
                    dateSegment.redemptions.length > 1
                "
                class="absolute left-6 top-6 bottom-0 w-px bg-slate-200 dark:bg-slate-700 z-0"
                :style="{ height: `calc(100% - 24px)` }"
            ></div>

            <!-- Using grid with explicit gap for consistent spacing -->
            <div class="grid grid-cols-1 gap-4">
                <div
                    v-for="(redemption, index) in dateSegment.redemptions"
                    :key="redemption"
                    class="relative"
                >
                    <div class="flex items-start gap-2">
                        <div class="relative z-10">
                            <div class="bg-slate-100 dark:bg-transparent p-2">
                                <div
                                    class="w-11 h-11 flex bg-[#fff1e4] dark:bg-slate-900/80 text-[#ff7d01] dark:text-white rounded-[100px] border-2 border-white dark:border-slate-800 justify-center items-center relative z-10"
                                >
                                    <RedemptionIcon :redemption="redemption" />
                                </div>
                            </div>
                        </div>
                        <div class="relative w-full">
                            <RedemptionCard
                                :redemption="redemption"
                                :read-only="readOnly"
                                @viewRedemption="viewRedemption"
                                :trip="trip"
                                :hide-sensitive-data="sensitiveDataHidden"
                            />

                            <!-- Combined Sub-row for both Companion Pass and Travel Portal -->
                            <div
                                v-if="
                                    redemption.date_type === 'start' &&
                                    ((redemption.redemption_type.slug ===
                                        'flight' &&
                                        isSouthwestFlight(redemption) &&
                                        wasCompanionPassUsed(redemption)) ||
                                        redemption.travel_portal_id)
                                "
                                class="hidden sm:flex flex-col justify-end items-start gap-2.5 self-stretch bg-slate-50 dark:bg-slate-800/70 border border-t-0 border-slate-200 dark:border-slate-700 px-4 pb-1.5 rounded-b-lg absolute -bottom-2 left-0 right-0 -z-10 h-24"
                            >
                                <div class="flex items-center gap-6">
                                    <!-- Travel Portal Info -->
                                    <div
                                        v-if="redemption.travel_portal_id"
                                        class="flex items-center gap-2"
                                    >
                                        <travel-portal-icon
                                            :portal="
                                                getPortalCode(
                                                    redemption.travel_portal_id
                                                )
                                            "
                                        />
                                        <span
                                            class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5"
                                        >
                                            {{
                                                getPortalName(
                                                    redemption.travel_portal_id,
                                                    redemption
                                                )
                                            }}
                                        </span>
                                    </div>

                                    <!-- Divider if both are present -->
                                    <div
                                        v-if="
                                            redemption.travel_portal_id &&
                                            redemption.redemption_type.slug ===
                                                'flight' &&
                                            isSouthwestFlight(redemption) &&
                                            wasCompanionPassUsed(redemption)
                                        "
                                        class="h-5 w-px bg-slate-300 dark:bg-slate-600"
                                    ></div>

                                    <!-- Southwest Companion Pass Info -->
                                    <div
                                        v-if="
                                            redemption.redemption_type.slug ===
                                                'flight' &&
                                            isSouthwestFlight(redemption) &&
                                            wasCompanionPassUsed(redemption)
                                        "
                                        class="flex items-center gap-1"
                                    >
                                        <svg
                                            width="95"
                                            height="20"
                                            viewBox="0 0 95 20"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <g clip-path="url(#clip0_834_6878)">
                                                <path
                                                    d="M25.3199 13.1178C24.9369 13.1899 24.49 13.2531 24.0093 13.2531C22.6659 13.2514 22.37 12.7856 22.3704 11.9091V6.74993H19.8426L19.8376 12.1641C19.8355 14.5653 21.1705 15.6658 23.8877 15.6688C25.2059 15.6696 26.4319 15.4244 27.8708 15.0541V6.74993H25.3199V13.1178ZM33.5257 13.3545C32.6065 13.3537 32.4799 12.9176 32.4803 12.2184L32.4832 8.89024L34.58 8.89236V6.74993H32.4832V4.20066H29.9512V6.74951L28.6739 6.74993V8.88684L29.9512 8.88811L29.9483 12.383C29.947 13.9218 30.2572 15.6756 32.7715 15.6777C33.5405 15.6785 34.1688 15.4919 34.8557 15.2959L34.604 13.1738C34.232 13.2824 33.9062 13.3545 33.5257 13.3545ZM6.98827 8.58351C6.51984 8.40278 5.99711 8.23308 5.4365 8.07696C3.96092 7.65696 3.50679 7.42236 3.50721 6.76945C3.50763 5.99817 4.07539 5.64393 5.39863 5.64521C6.48575 5.64605 7.45166 5.92945 8.43398 6.30151L8.96513 3.8023C7.83297 3.37721 6.75763 2.99836 5.45334 2.99751C2.40789 2.99496 0.728172 4.73902 0.726068 6.83181C0.724805 8.38199 1.41967 9.43496 2.91883 10.0459C3.35906 10.2249 3.86664 10.4022 4.42893 10.569C6.08213 11.0806 6.56361 11.3203 6.56319 11.9613C6.56277 12.7385 5.93272 13.1526 4.63726 13.1513C3.44661 13.1501 2.35191 12.82 1.20713 12.2893L0.677246 14.8105C1.92683 15.3078 3.17135 15.6764 4.66841 15.6777C7.76689 15.6807 9.37422 13.9319 9.3759 11.8328C9.37758 10.1163 8.41714 9.1346 6.98827 8.58351ZM40.1461 6.3083C39.3161 6.30745 38.5232 6.50175 37.9605 6.6986V2.49902H35.4323V15.6561H37.9525L37.958 9.06078C38.4138 8.88472 38.9399 8.71078 39.5619 8.71121C40.5464 8.71205 40.9024 9.0926 40.9016 10.0196L40.8969 15.6561H43.4176L43.4222 9.74254C43.4243 7.41811 42.3831 6.30999 40.1461 6.3083ZM14.5535 6.2663C11.9971 6.26417 9.93356 8.07357 9.93103 10.933C9.92851 13.7932 11.982 15.6756 14.5451 15.6777C17.1065 15.6802 19.1634 13.8081 19.1659 10.9419C19.1684 8.07569 17.1078 6.26884 14.5535 6.2663ZM14.5472 13.1747C13.4483 13.1738 12.5682 12.3779 12.5695 10.9368C12.5708 9.49563 13.4235 8.73581 14.551 8.73666C15.6768 8.73751 16.5114 9.48799 16.5102 10.9393C16.5093 12.3928 15.6453 13.1764 14.5472 13.1747ZM71.2446 10.2334C70.8847 10.1074 70.4803 9.97714 70.0396 9.84902C69.1541 9.61399 68.8107 9.46423 68.8111 9.09642C68.8115 8.70823 69.1541 8.44987 69.9327 8.4503C70.7686 8.45114 71.5388 8.62466 72.3641 8.86563L72.766 6.66466C71.8561 6.46399 70.955 6.28751 69.934 6.28624C67.54 6.28411 66.2513 7.51908 66.2496 9.20333C66.2488 10.3997 66.825 11.1557 68.0186 11.6644C68.3557 11.809 68.7522 11.9554 69.2021 12.0975C70.1352 12.369 70.5897 12.4793 70.5897 12.904C70.5889 13.3083 70.0367 13.492 69.2959 13.4911C68.3843 13.4903 67.5333 13.3405 66.6549 12.9914L66.2618 15.1779C67.3494 15.4902 68.3696 15.6955 69.5013 15.6964C72.0186 15.6989 73.1478 14.3693 73.1495 12.7288C73.1507 11.6058 72.6705 10.7382 71.2446 10.2334ZM61.7496 6.27648C59.0779 6.27393 57.2244 8.15078 57.2219 11.1073C57.2197 13.8658 58.8725 15.6853 61.7938 15.6879C63.0994 15.6887 64.1638 15.3201 65.2585 14.8504L64.8788 12.7432C64.0539 13.1174 63.2008 13.4088 62.2046 13.408C60.6987 13.4063 60.0783 12.7542 59.9811 11.8328L65.6558 11.8379C65.6558 11.8379 65.7378 10.8002 65.7378 10.5982C65.7412 8.19745 64.4209 6.2786 61.7496 6.27648ZM60.0173 10.132C60.1785 9.35054 60.5821 8.38242 61.644 8.38327C62.7383 8.38454 63.0678 9.50327 63.096 10.1345L60.0173 10.132ZM78.1356 13.3545C77.2164 13.3537 77.0947 12.9176 77.0956 12.2184L77.099 8.9106L79.2197 8.91272V6.74993H77.0981V4.20066H74.5615V6.74951L73.2724 6.74993V8.9072L74.5615 8.90848L74.5581 12.383C74.5564 13.9218 74.9272 15.6756 77.4416 15.6777C78.2105 15.6785 78.8443 15.4919 79.5316 15.2959L79.2193 13.1738C78.8477 13.2824 78.5165 13.3545 78.1356 13.3545ZM79.8191 3.02678C79.2042 3.02636 78.7038 3.53078 78.7029 4.15314C78.7021 4.77551 79.2029 5.28036 79.8165 5.28121C80.4348 5.28163 80.9352 4.77721 80.9361 4.15484C80.9369 3.53248 80.4365 3.02763 79.8191 3.02678ZM79.8165 5.08181C79.2989 5.08139 78.9125 4.70339 78.9125 4.15357C78.9134 3.60672 79.3006 3.22575 79.8182 3.2266C80.3401 3.22702 80.724 3.60799 80.7231 4.15484C80.7235 4.70466 80.3389 5.08181 79.8165 5.08181ZM79.9554 4.20533C80.1478 4.17563 80.277 4.06066 80.277 3.86424C80.277 3.64957 80.1276 3.48624 79.8426 3.48581L79.4016 3.48539L79.4007 4.79375L79.6406 4.79417V4.23502C79.7277 4.23545 79.7606 4.26217 79.8208 4.35933L80.0994 4.7946H80.3746L80.1082 4.38945C80.0485 4.29908 80.0043 4.24181 79.9554 4.20533ZM79.7673 4.06024L79.6406 4.05981L79.6415 3.67927L79.8064 3.67969C79.9689 3.67969 80.0257 3.75563 80.0257 3.87017C80.0249 4.03011 79.9024 4.06024 79.7673 4.06024ZM54.8776 6.74993L53.2976 11.6491L51.6617 6.74993H49.4571L47.8216 11.6491L46.2412 6.74993H43.4942L46.631 15.6552H48.9622L50.5724 10.8354L52.1575 15.6552H54.4887L57.6251 6.74993H54.8776Z"
                                                    fill="#304CB2"
                                                />
                                                <path
                                                    d="M90.4508 4.75C89.4483 4.75 88.6409 5.09932 87.9957 5.46642C87.3499 5.09932 86.5441 4.75 85.5411 4.75C83.5366 4.75 82.2129 6.05933 82.2129 8.29655C82.2129 11.0259 84.8192 13.6925 87.9957 15.8333C91.1726 13.692 93.7785 11.0264 93.7785 8.29655C93.7785 6.05982 92.4558 4.75 90.4508 4.75Z"
                                                    fill="#CCCCCC"
                                                />
                                                <path
                                                    d="M82.541 8.34413C82.541 11.1347 85.4838 13.7307 87.9967 15.4491C88.72 14.954 89.4671 14.3858 90.1781 13.7613C86.849 10.9727 83.7244 8.47802 82.6033 7.58472C82.5633 7.82336 82.541 8.07781 82.541 8.34413Z"
                                                    fill="url(#paint0_linear_834_6878)"
                                                />
                                                <path
                                                    d="M93.2292 9.63939C90.8165 7.6497 89.3555 6.63683 87.7888 5.69658C87.3668 5.44163 86.5195 5.06909 85.5693 5.06909C84.6592 5.06909 83.8949 5.3522 83.3682 5.9041C83.0253 6.26231 82.7926 6.71242 82.6597 7.25839C83.9092 8.13094 87.3021 10.5569 90.7197 13.2635C91.861 12.177 92.8171 10.9413 93.2292 9.63939Z"
                                                    fill="url(#paint1_linear_834_6878)"
                                                />
                                                <path
                                                    d="M92.6175 5.90967C92.0987 5.36914 91.3383 5.07812 90.4633 5.07812C89.5245 5.07812 88.8091 5.38149 88.3105 5.65225C89.2463 6.17302 91.2795 7.35141 93.3858 8.90284C93.4184 8.69335 93.4367 8.48287 93.4367 8.2714C93.4362 7.28867 93.1491 6.46552 92.6175 5.90967Z"
                                                    fill="url(#paint2_linear_834_6878)"
                                                />
                                            </g>
                                            <defs>
                                                <linearGradient
                                                    id="paint0_linear_834_6878"
                                                    x1="82.5412"
                                                    y1="11.5167"
                                                    x2="90.1783"
                                                    y2="11.5167"
                                                    gradientUnits="userSpaceOnUse"
                                                >
                                                    <stop
                                                        stop-color="#3656CC"
                                                    />
                                                    <stop
                                                        offset="0.35"
                                                        stop-color="#304CB2"
                                                    />
                                                    <stop
                                                        offset="0.72"
                                                        stop-color="#283B80"
                                                    />
                                                </linearGradient>
                                                <linearGradient
                                                    id="paint1_linear_834_6878"
                                                    x1="83.6259"
                                                    y1="5.48437"
                                                    x2="92.2533"
                                                    y2="11.5254"
                                                    gradientUnits="userSpaceOnUse"
                                                >
                                                    <stop
                                                        offset="0.3"
                                                        stop-color="#E61C23"
                                                    />
                                                    <stop
                                                        offset="0.8"
                                                        stop-color="#C3161C"
                                                    />
                                                </linearGradient>
                                                <linearGradient
                                                    id="paint2_linear_834_6878"
                                                    x1="88.9716"
                                                    y1="4.70825"
                                                    x2="93.9042"
                                                    y2="8.16216"
                                                    gradientUnits="userSpaceOnUse"
                                                >
                                                    <stop
                                                        stop-color="#FDBC11"
                                                    />
                                                    <stop
                                                        offset="0.25"
                                                        stop-color="#FCB415"
                                                    />
                                                    <stop
                                                        offset="0.8"
                                                        stop-color="#F89E1C"
                                                    />
                                                </linearGradient>
                                                <clipPath id="clip0_834_6878">
                                                    <rect
                                                        width="95"
                                                        height="14"
                                                        fill="white"
                                                        transform="translate(0 2)"
                                                    />
                                                </clipPath>
                                            </defs>
                                        </svg>
                                        <span
                                            class="text-slate-800 dark:text-slate-100 text-sm font-normal leading-5"
                                        >
                                            Companion Pass
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { useTripsStore } from '@/store/trips';

import RedemptionCard from '@/components/trips/redemptions/redemption-card.vue';
import RedemptionIcon from '@/components/trips/redemptions/redemption-icon.vue';
import TravelPortalIcon from '@/components/ui/icon/travel-portal-icon.vue';
import { computed, onMounted, ref } from 'vue';

const props = defineProps({
    dateSegment: {
        type: Object,
        required: true,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    trip: {
        type: Object,
        default: {},
    },
    hideSensitiveData: {
        type: Boolean,
        default: false,
    },
});

const tripsStore = useTripsStore();
const travelPortals = computed(() => tripsStore.travelPortals);

onMounted(async () => {
    await tripsStore.getTravelPortals();
});

const getPortalCode = (portalId, currentRedemption) => {
    if (!portalId || !travelPortals.value || !travelPortals.value.length)
        return 'other';

    const portal = travelPortals.value.find((p) => p.id === portalId);
    return portal ? portal.code : 'other';
};

const getPortalName = (portalId, currentRedemption) => {
    if (!portalId || !travelPortals.value || !travelPortals.value.length)
        return 'Travel Portal';

    const travelPortal = travelPortals.value.find((p) => p.id === portalId);

    if (travelPortal?.code === 'other') {
        return (
            currentRedemption.travel_portal_custom_name ||
            currentRedemption.travel_portal?.name ||
            'Travel Portal'
        );
    }

    return travelPortal ? travelPortal.name : 'Travel Portal';
};

const viewRedemption = (redemption) => {
    tripsStore.setPanelRedemption(redemption);
    tripsStore.setActiveRedemption(redemption);
    tripsStore.setShowRedemptionPanel(true);
};

const sensitiveDataHidden = computed(() => {
    return props.readOnly && props.hideSensitiveData;
});

const isSouthwestFlight = (redemption) => {
    if (!redemption.travel_segments || !redemption.travel_segments.length) {
        return false;
    }

    const isSW = redemption.travel_segments.some(
        (segment) =>
            segment.travel_number &&
            segment.travel_number.toLowerCase().startsWith('wn')
    );
    return isSW;
};

const wasCompanionPassUsed = (redemption) => {
    if (
        redemption.payment_type !== 'cash' &&
        redemption.payment_type !== 'points' &&
        redemption.payment_type !== 'other'
    ) {
        return false;
    }

    if (!redemption.travel_segments || !redemption.travel_segments.length) {
        return false;
    }

    const hasSouthwestSegment = redemption.travel_segments.some(
        (segment) =>
            segment.travel_number &&
            segment.travel_number.toLowerCase().startsWith('wn')
    );

    if (!hasSouthwestSegment) {
        return false;
    }

    return redemption.companion_pass_used === true;
};
</script>
